/** @jsx jsx */
import { jsx, css } from '@compiled/react';

import { readableTextColor } from '@helpcenter/styles/src/colors';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { token } from '@atlaskit/tokens';
import { AiSearchTrigger } from '@atlassian/csm-ai-search-trigger/ai-search-trigger';
import { useOptionalAiWidgetConfig } from '@atlassian/csm-ai-search-trigger/controllers/ai-widget-config-context';

const textContainerStyles = css({
	height: '56px',
	alignSelf: 'center',
	justifySelf: 'center',
	width: '90%',
});

const outerStackWrapperStyles = css({
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexDirection: 'column',
});

const bannerTextStyles = css({
	fontWeight: token('font.weight.bold', '700'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '48px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '56px',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	width: '100%',
	textAlign: 'center',
});

const wrapperStyles = css({
	display: 'flex',
	flexDirection: 'column',
});

const bannerStyles = css({
	paddingTop: token('space.1000', '80px'),
	paddingBottom: token('space.1000', '80px'),
	borderRadius: token('radius.small', '4px'),
	marginLeft: token('space.300', '24px'),
	marginRight: token('space.300', '24px'),
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
});

const aiSearchTriggerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	transform: 'translateY(-50%)',
});

interface CsmBannerTextAsTranslatableMessage {
	id: string;
	defaultMessage: string;
	description: string;
}

export type CsmBannerTextTypeProps = string | CsmBannerTextAsTranslatableMessage;

interface CsmBannerProps {
	bannerText?: CsmBannerTextTypeProps;
	bannerBackgroundColor: string;
}

export const CsmBanner = ({ bannerText, bannerBackgroundColor }: CsmBannerProps) => {
	di(useOptionalAiWidgetConfig, AiSearchTrigger, readableTextColor);
	const aiWidgetConfig = useOptionalAiWidgetConfig();
	const { formatMessage, locale } = useIntl();

	const renderAiTriggerWithBanner =
		aiWidgetConfig &&
		aiWidgetConfig.supportWidgetConfig.isEnabled &&
		aiWidgetConfig.supportWidgetConfig.widgetPosition === 'CENTER';

	let bannerTextToDisplay = '';

	if (bannerText && typeof bannerText === 'string') {
		bannerTextToDisplay = bannerText;
	} else if (bannerText && typeof bannerText === 'object') {
		bannerTextToDisplay = formatMessage(bannerText);
	}

	return (
		<div css={wrapperStyles}>
			<div
				css={bannerStyles}
				style={{
					backgroundColor: bannerBackgroundColor,
				}}
				data-testid="csm-banner"
			>
				<div css={outerStackWrapperStyles} data-testid="csm-banner-outer-stack-wrapper">
					<div css={textContainerStyles} data-testid="csm-banner-text-container">
						<div
							css={bannerTextStyles}
							data-testid="banner-text"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							style={{ color: readableTextColor(bannerBackgroundColor) }}
						>
							{bannerTextToDisplay}
						</div>
					</div>
				</div>
			</div>
			{renderAiTriggerWithBanner && (
				<div css={aiSearchTriggerStyles} data-testid="ai-search-trigger-container">
					<AiSearchTrigger
						locale={locale}
						site={aiWidgetConfig.supportWidgetConfig.site}
						siteId={aiWidgetConfig.supportWidgetConfig.siteId}
						widgetId={aiWidgetConfig.supportWidgetConfig.widgetId}
						widgetTheme={aiWidgetConfig.supportWidgetConfig.widgetTheme}
					/>
				</div>
			)}
		</div>
	);
};
